import type { FC } from "react"
import { useTranslation } from "react-i18next"
import { Box } from "@mui/material"
import type { DateTime } from "luxon"

import type { DateRefOption } from "helpers/hooks/useSelectOptions"
import ForecastChip from "./ForecastChip"
import { formatRangeLabel } from "./helpers"
import { usePickerLocale } from "./hooks"

interface DateRangeTriggerLabelProps {
  option?: DateRefOption
  zoom?: boolean
  /** The applied dates, written out when the range is a custom one. */
  fromDate: DateTime
  toDate: DateTime
}

/**
 * The selected reference on a date picker's button, tagged when it is a forecast; a custom
 * range shows its dates instead.
 */
const DateRangeTriggerLabel: FC<DateRangeTriggerLabelProps> = ({
  option,
  zoom = false,
  fromDate,
  toDate,
}) => {
  const { t } = useTranslation()
  const locale = usePickerLocale()
  const label =
    option && option.value !== "custom"
      ? t(option.label)
      : formatRangeLabel(fromDate, toDate, locale)

  return (
    <Box
      component="span"
      // The same text as the options in the popover, brighter than the button's icons.
      sx={{ display: "inline-flex", alignItems: "center", gap: 1, color: "text.primary" }}
    >
      {zoom ? `${label} (${t("dates.ZOOMED_IN")})` : label}
      {option?.forecastTime && <ForecastChip forecastTime={option.forecastTime} />}
    </Box>
  )
}

export default DateRangeTriggerLabel
